import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from '../../icons';
import { useOutsideAlerter } from '../../utils/useOutsideAlerter';

import { CheckBox } from '../CheckBox';
import { TextField } from '../TextField';
import { Paragraph } from '../Typography';
import { Container, DropDownOptions, NoResultsContainer } from './MultiSelect.styles';
import { MultiSelectOption, MultiSelectProps } from './MultiSelect.types';

export const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  style,
  dropdownInputStyle,
  label,
  options,
  disabled,
  size,
  width,
  onChange,
  selectedOptionsLabel = 'Selecionados',
  unselectedOptionsLabel = 'Outros',
  noResultsText = 'Não há itens que correspondem à pesquisa',
  ...props
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [showDropDown, setShowDropDown] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const selectedOptions = useMemo(
    () => options.filter((option: MultiSelectOption) => option.checked),
    [options],
  );
  const filteredOptions = useMemo(() => {
    return options.filter(currentValue =>
      currentValue.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [searchValue, options]);

  const selectedFilteredOptions = useMemo(
    () => filteredOptions.filter((option: MultiSelectOption) => option.checked),
    [filteredOptions],
  );
  const unSelectedFilteredOptions = useMemo(
    () => filteredOptions.filter((option: MultiSelectOption) => !option.checked),
    [filteredOptions],
  );

  const handleChangeSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const search = e.target.value;
    setSearchValue(search);
  };

  const changeOptionValue = (options: MultiSelectOption[], optionId: string) =>
    options.map(option =>
      option.id === optionId ? { ...option, checked: !option.checked } : option,
    );

  const toggleOption = useCallback(
    optionId => {
      onChange(changeOptionValue(options, optionId));
    },
    [onChange, options],
  );

  const openDropDown = () => {
    setShowDropDown(true);
  };

  const closeDropDown = () => {
    setShowDropDown(false);
    setSearchValue('');
  };

  useOutsideAlerter(wrapperRef, closeDropDown);

  useEffect(() => {
    disabled && setShowDropDown(false);
  }, [disabled]);

  return (
    <Container ref={wrapperRef} onClick={openDropDown} style={style} {...props}>
      <TextField
        ref={inputRef}
        data-testid={'multiselect-dropdown-input'}
        value={showDropDown ? searchValue : `${selectedOptions.length} valores selecionados`}
        label={label}
        trailingIcon={showDropDown ? ChevronUp : ChevronDown}
        size={size}
        onChange={handleChangeSearchValue}
        width={width ?? '100%'}
        autoComplete="off"
        disabled={disabled}
        style={dropdownInputStyle}
      />
      <DropDownOptions data-testid="multiselect-options" show={showDropDown}>
        {filteredOptions.length ? (
          <>
            {selectedFilteredOptions.length !== 0 && <p>{selectedOptionsLabel}</p>}
            {selectedFilteredOptions.map(option => (
              <CheckBox
                key={`${id ? id + '-' : ''}${option.id.toString()}`}
                id={`${id ? id + '-' : ''}${option.id.toString()}`}
                checked={option.checked}
                onChange={() => toggleOption(option.id)}
                label={option.label}
                disabled={option.unselectable}
                style={{ marginBottom: '12px' }}
              />
            ))}
            {unSelectedFilteredOptions.length !== 0 && <p>{unselectedOptionsLabel}</p>}
            {unSelectedFilteredOptions.map(option => (
              <CheckBox
                key={`${id ? id + '-' : ''}${option.id.toString()}`}
                id={`${id ? id + '-' : ''}${option.id.toString()}`}
                checked={option.checked}
                onChange={() => toggleOption(option.id)}
                label={option.label}
                disabled={option.unselectable}
                style={{ marginBottom: '12px' }}
              />
            ))}
          </>
        ) : (
          <NoResultsContainer data-testid="no-results">
            <Paragraph variant="p2" style={{ margin: 0 }}>
              {noResultsText}
            </Paragraph>
          </NoResultsContainer>
        )}
      </DropDownOptions>
    </Container>
  );
};
